import React, { memo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { OverviewContextProvider } from 'site-modules/personalized-search/context/overview-context/overview-context-provider';
import { SearchErrorScreen } from 'site-modules/personalized-search/components/search-error-screen/search-error-screen';
import { SearchRenderer } from 'site-modules/personalized-search/search-renderer/search-renderer';
import {
  ERROR_STATUSES,
  PersonalizedSearchModel,
  PersonalizedSearchPaths,
} from 'client/data/models/personalized-search';
import { fireWidgetViewTracking } from 'site-modules/shared/utils/inventory/global-search-tracking';
import { fireWidgetViewTracking as fireWidgetViewLoadTracking } from 'site-modules/shared/utils/personalized-search/personalized-search-tracking';
import { EventToolbox } from 'client/utils/event-toolbox';
import { PAGE_ASYNC_CONTENT_LOAD } from 'client/tracking/constant';
import { WelcomeScreen } from 'site-modules/personalized-search/components/welcome-screen/welcome-screen';
import {
  EXPIRED_KEY_RESULT_CREATIVE_ID,
  FALLBACK_RESULT_CREATIVE_ID,
} from 'site-modules/personalized-search/constants/tracking';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { useLoadingCoordinator } from 'site-modules/personalized-search/hooks/use-loading-coordinator';
import { CsatSearchSurvey } from 'site-modules/personalized-search/components/csat-search-survey/csat-search-survey';
import {
  LAST_TOKEN_CREATIVE_ID,
  LOADING_SCREEN_CREATIVE_ID,
} from 'site-modules/shared/constants/personalized-search/personalized-search';
import { useSearchResults } from 'site-modules/personalized-search/hooks/use-search-results';

const PersonalizedSearchContentUI = memo(({ isMobile = false, isExpired = false }) => {
  const { isFullyLoaded } = useLoadingCoordinator();
  const { pageStruct, searchId, isPageStructLoading } = useSearchResults();
  const isError = pageStruct === null;

  const containerRef = useRef(null);

  const showWelcomeScreen = !(isError || pageStruct?.components?.length || isPageStructLoading);

  useEffect(() => {
    if (isFullyLoaded) {
      EventToolbox.fireCustomEvent(PAGE_ASYNC_CONTENT_LOAD, { target: containerRef.current });

      fireWidgetViewLoadTracking({ creativeId: LAST_TOKEN_CREATIVE_ID });
    }
  }, [isFullyLoaded]);

  useEffect(() => {
    // Error screen tracking
    if (isError) {
      fireWidgetViewTracking({
        value: `srp_search_id=${searchId}`,
        creativeId: isExpired ? EXPIRED_KEY_RESULT_CREATIVE_ID : FALLBACK_RESULT_CREATIVE_ID,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  useEffect(() => {
    if (isPageStructLoading) {
      fireWidgetViewLoadTracking({ creativeId: LOADING_SCREEN_CREATIVE_ID });
    }
  }, [isPageStructLoading]);

  const showSurvey = isFullyLoaded && !!pageStruct?.components?.length;

  return (
    <div ref={containerRef}>
      <h1 className="visually-hidden">Personalised Search</h1>
      {isError && <SearchErrorScreen isExpired={isExpired} />}
      {showWelcomeScreen && <WelcomeScreen />}
      {!isError && (
        <OverviewContextProvider pageStruct={pageStruct}>
          <div>
            <SearchRenderer componentParams={pageStruct} isMobile={isMobile} />
            {showSurvey && <CsatSearchSurvey searchId={searchId} />}
          </div>
        </OverviewContextProvider>
      )}
    </div>
  );
});

PersonalizedSearchContentUI.displayName = 'PersonalizedSearchContentUI';

PersonalizedSearchContentUI.propTypes = {
  isMobile: PropTypes.bool,
  isExpired: PropTypes.bool,
};

export const PersonalizedSearchContent = connectToModel(PersonalizedSearchContentUI, {
  isExpired: bindToPath(
    PersonalizedSearchPaths.getSearchError(),
    PersonalizedSearchModel,
    error => error === ERROR_STATUSES.EXPIRED
  ),
});
